// notify.ts -- per-member sound preferences + bubble-pop SFX.
//
// Sound mute is per-MEMBER, not global, so a noisy peer can be muted
// without losing the affordance for everyone else. Default ON.
// Persisted in localStorage so the choice survives reloads.
//
// Bubble-pop is synthesized via the WebAudio API (no asset to fetch)
// so the first invocation lands instantly with no flash of silence
// while a download completes. The synthesis is a quick downward
// frequency sweep with a short envelope -- recognisable as a "pop"
// without stealing focus.
//
// DND mode is owned by the activity-status token (next push) but
// surfaces here as a single boolean check `isDndActive()` that v1
// reads from localStorage so the toggle works without the token
// formation existing yet. The token will become the source of truth
// in a follow-up; the API stays the same.
const SOUND_PREFS_KEY = 'fdn:sound-prefs';
const DND_KEY = 'fdn:dnd';
function readPrefs() {
    try {
        const raw = localStorage.getItem(SOUND_PREFS_KEY);
        if (!raw)
            return { mutedMemberIds: {} };
        const parsed = JSON.parse(raw);
        return { mutedMemberIds: parsed.mutedMemberIds ?? {} };
    }
    catch {
        return { mutedMemberIds: {} };
    }
}
function writePrefs(p) {
    try {
        localStorage.setItem(SOUND_PREFS_KEY, JSON.stringify(p));
    }
    catch {
        // localStorage full / disabled / private mode -- ignore
    }
}
export function isMemberMuted(memberId) {
    return Boolean(readPrefs().mutedMemberIds[memberId]);
}
export function setMemberMuted(memberId, muted) {
    const p = readPrefs();
    if (muted) {
        p.mutedMemberIds[memberId] = true;
    }
    else {
        delete p.mutedMemberIds[memberId];
    }
    writePrefs(p);
}
// ---------------------------------------------------------------------------
// DND -- v1 reads localStorage. v2 reads the activity-status token.
// Same API both ways so callers don't change.
// ---------------------------------------------------------------------------
export function isDndActive() {
    return localStorage.getItem(DND_KEY) === '1';
}
export function setDndActive(active) {
    if (active)
        localStorage.setItem(DND_KEY, '1');
    else
        localStorage.removeItem(DND_KEY);
}
// ---------------------------------------------------------------------------
// Bubble-pop SFX.
// ---------------------------------------------------------------------------
let _ctx = null;
function getCtx() {
    if (_ctx)
        return _ctx;
    try {
        const Ctor = window.AudioContext
            ?? window.webkitAudioContext;
        if (!Ctor)
            return null;
        _ctx = new Ctor();
        return _ctx;
    }
    catch {
        return null;
    }
}
/**
 * Play a short downward-sweep "bubble-pop" via WebAudio. Honours
 * per-member mute and global DND. Safe to call from anywhere; the
 * AudioContext is created lazily on first use and reused.
 *
 * Returns true if the sound was played, false if it was suppressed
 * (muted member, DND active, AudioContext unavailable, or browser
 * autoplay policy hasn't unlocked the context yet).
 */
export function playBubblePop(opts = {}) {
    if (isDndActive())
        return false;
    if (opts.memberId && isMemberMuted(opts.memberId))
        return false;
    const ctx = getCtx();
    if (!ctx)
        return false;
    // Most browsers gate audio behind a user gesture. If the context
    // isn't running we kick off a resume (best-effort) but don't await
    // it -- a poll-driven sound shouldn't stall waiting on the user to
    // tap somewhere. If the context is still suspended after the
    // attempt, give up silently; the next user click on the dock
    // (mute toggle, avatar) will unlock the context naturally and
    // subsequent sounds will play.
    if (ctx.state !== 'running') {
        void ctx.resume().catch(() => undefined);
        // Re-read state through an unconstrained local; ctx.state is a
        // narrow type immediately after the comparison above.
        const stateNow = ctx.state;
        if (stateNow !== 'running')
            return false;
    }
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    // Downward frequency sweep: 880Hz -> 220Hz over 90ms, sine wave.
    // Recognisable as a quick blip, doesn't sound alarming.
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(220, now + 0.09);
    // Envelope: fast attack, exp decay so it doesn't click on either end.
    const peak = 0.18;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    osc.start(now);
    osc.stop(now + 0.13);
    return true;
}
